import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium tracking-tight transition-[background-color,color,border-color,transform] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-glow focus-visible:ring-offset-2 focus-visible:ring-offset-bone disabled:pointer-events-none disabled:opacity-50 active:scale-[0.985]',
  {
    variants: {
      variant: {
        // All filled variants share the same orange treatment now. `accent`
        // and `action` are kept as aliases of `default` so existing callsites
        // continue to compile without sweeping the codebase.
        default: 'bg-action text-bone shadow-sm hover:bg-action-hover hover:shadow-md',
        accent: 'bg-action text-bone shadow-sm hover:bg-action-hover hover:shadow-md',
        action: 'bg-action text-bone shadow-sm hover:bg-action-hover hover:shadow-md',
        outline: 'border border-bone-deep bg-transparent text-ink hover:bg-bone-warm',
        ghost: 'bg-transparent text-ink hover:bg-bone-warm',
        link: 'bg-transparent text-ink underline-offset-4 hover:underline hover:decoration-action',
        // Destructive stays red on purpose — orange "Delete" buttons are an
        // accidental-deletion hazard. Override only if you really want to.
        destructive: 'bg-destructive text-bone hover:opacity-90',
      },
      size: {
        default: 'h-11 px-5',
        sm: 'h-9 px-3 text-xs',
        lg: 'h-12 px-7 text-base',
        pill: 'h-11 px-6 rounded-full',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
